import dayjs from "dayjs";
import {
  ComponentProps,
  forwardRef,
  useImperativeHandle,
} from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { Button } from "@design-system/Button/Button";
import { DateRangePicker } from "@design-system/DateRangePicker/DateRangePicker";
import { FieldGroup } from "@design-system/FieldGroup/FieldGroup";
import { Form } from "@design-system/Form/Form";
import { InfiniteCategorySelect } from "@components/Selects/InfiniteCategorySelect";

type DateRange = [Date | null, Date | null];

// Helper function to convert date range to form values
const dateRangeToFormValues = (
  range: DateRange,
): { createdAt_Gte?: string; createdAt_Lte?: string } => {
  const [startDate, endDate] = range;
  return {
    createdAt_Gte: startDate ? dayjs(startDate).format("YYYY-MM-DD") : undefined,
    createdAt_Lte: endDate ? dayjs(endDate).format("YYYY-MM-DD") : undefined,
  };
};

// Helper function to convert form values to date range
const formValuesToDateRange = (
  createdAt_Gte?: string,
  createdAt_Lte?: string,
): DateRange => {
  return [
    createdAt_Gte ? dayjs(createdAt_Gte).toDate() : null,
    createdAt_Lte ? dayjs(createdAt_Lte).toDate() : null,
  ];
};

export type EmployeeApplicationFilterFormValues = {
  parentCategoryId?: string | undefined;
  createdAt_Gte?: string | undefined;
  createdAt_Lte?: string | undefined;
};

export type EmployeeApplicationFilterFormRef = {
  getCurrentValues: () => EmployeeApplicationFilterFormValues;
};

type Props = {
  submitText?: string;
  onFilterChange: SubmitHandler<EmployeeApplicationFilterFormValues>;
  values: EmployeeApplicationFilterFormValues;
  errorsAPI?: ComponentProps<typeof Form>["errorsAPI"];
  disabled?: boolean;
  withSubmitButton?: boolean;
};

export const EmployeeApplicationFilterForm = forwardRef<
  EmployeeApplicationFilterFormRef,
  Props
>(({ onFilterChange, values, errorsAPI }, ref) => {
  const { handleSubmit, setValue, watch, getValues } =
    useForm<EmployeeApplicationFilterFormValues>({
      values: {
        parentCategoryId: values.parentCategoryId ?? undefined,
        createdAt_Gte: values.createdAt_Gte ?? undefined,
        createdAt_Lte: values.createdAt_Lte ?? undefined,
      },
    });

  const parentCategoryId = watch("parentCategoryId");
  const createdAt_Gte = watch("createdAt_Gte");
  const createdAt_Lte = watch("createdAt_Lte");

  // Handle date range changes
  const handleDateRangeChange = (range: DateRange) => {
    const { createdAt_Gte, createdAt_Lte } = dateRangeToFormValues(range);
    setValue("createdAt_Gte", createdAt_Gte, { shouldDirty: true });
    setValue("createdAt_Lte", createdAt_Lte, { shouldDirty: true });
  };

  // Expose getCurrentValues method to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      getCurrentValues: () => getValues(),
    }),
    [getValues],
  );

  return (
    <Form onSubmit={handleSubmit(onFilterChange)} errorsAPI={errorsAPI}>
      <FieldGroup label="Category">
        <InfiniteCategorySelect
          value={parentCategoryId}
          onChange={(value) => setValue("parentCategoryId", value, { shouldDirty: true })}
          placeholder="Search and select category..."
        />
      </FieldGroup>

      <FieldGroup label="Activity between">
        <DateRangePicker
          value={formValuesToDateRange(createdAt_Gte, createdAt_Lte)}
          onChange={handleDateRangeChange}
          placeholder="Select date range"
        />
      </FieldGroup>

      {/* Hidden submit button to enable Enter key submission */}
      <Button type="submit" style={{ display: "none" }} />
    </Form>
  );
});
